// Replay viewer chrome, overlaid on a GameBoard mounted in replay mode: the
// full-height Chronicle sidebar (the event feed that doubles as the scrub
// readout), the floating playback deck (timeline + transport + speed), and
// the perspective control. The board itself renders behind this through the
// live spectator pipeline; this component only drives the ReplayDriver and
// reads its status.
//
// Interaction contract (deliberate, from the design pass): scrolling the feed
// only browses. Clicking a row seeks. Playback auto-centers the current row
// until the user scrolls, then a "Return to now" pill re-engages follow mode.
// Stepping and seeking snap the board (Fulls); playing animates it (diffs).
import { Component, Fragment } from 'preact';
import { motion } from 'framer-motion';

import { GOLD, GOLD_BTN, TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY, ACCENT_GOLD } from '../../lib/medievalTheme';
import { renderLogTextWithCells } from '../gameBoard/logFormatting';
import { parseCardMentions, rectToCanvasRect } from '../../utils/cardMentions';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { CommentRow, CommentComposer, continuesPrevious } from '../comments/CommentThread';
import { PopoverPanel, PopoverMenuItem } from '../ui/popoverMenu';
import { IconLink } from '../ui/icons';
import { resolveAvatarUrl } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import { t } from '../../localization/t';

// p1 gold matches the logbook's local-seat accent; p2 blue matches its
// opponent color, keyed here by seat rather than by locality.
const SEAT_COLORS = { p1: ACCENT_GOLD, p2: '#8ea8c8' };
const SPEEDS = [1, 2, 4];
// Wide enough that the header badges, the feed, and the footer transport all
// breathe; the playback controls live HERE (not floating over the board)
// so they never obscure the bottom player's hand.
const SIDEBAR_WIDTH = '24cqw';

// Feed rows derived once from the immutable view: turn markers become chapter
// headers, chron entries become event rows. `seq` is the frame count at which
// the row's event has just happened, so clicking a row seeks to its seq.
function buildRows(view) {
  const rows = [{ type: 'turn', n: 0, seat: null, seq: 0, noteKey: '0:turn' }];
  for (const frame of view.frames) {
    if (frame.turnStart) {
      rows.push({ type: 'turn', n: frame.turnStart.n, seat: frame.turnStart.seat, seq: frame.seq, noteKey: `${frame.seq}:turn`, t: frame.t });
    }
    (frame.chron || []).forEach((chron, idx) => {
      rows.push({ type: 'ev', seq: frame.seq, noteKey: `${frame.seq}:${idx}`, t: frame.t, ...chron });
    });
  }
  return rows;
}

// Match clock, "m:ss" (h:mm:ss past the hour). Undefined for bundles
// archived before timestamps rode the journal.
function formatClock(t) {
  if (typeof t !== 'number') return null;
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const sec = String(t % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

// Small sticky-note glyph for commented rows.
function NoteIcon({ filled }) {
  return (
    <svg style={{ width: '1.0625cqw', height: '1.0625cqw' }} viewBox="0 0 16 16" fill={filled ? 'rgba(212,168,67,0.28)' : 'none'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h10v6.5L9.5 13H3z" />
      <path d="M9.5 13V9.5H13" />
      <path d="M5.5 6h5M5.5 8.5h3" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="3.5" r="1.8" /><circle cx="4" cy="8" r="1.8" /><circle cx="12" cy="12.5" r="1.8" />
      <path d="M5.6 7.1 10.4 4.4M5.6 8.9l4.8 2.7" />
    </svg>
  );
}

// A speech bubble: the Tavern is the global chat.
function TavernIcon() {
  return (
    <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 3.5h11v7h-6L4.5 13v-2.5h-2z" />
    </svg>
  );
}

// Crossed swords for combat rows: fights are their own class of event in the
// feed, and the badge is what makes them scannable at a glance.
function CombatIcon() {
  return (
    <svg
      className="shrink-0 self-center"
      style={{ width: '0.875cqw', height: '0.875cqw', color: ACCENT_GOLD }}
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    >
      <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
      <line x1="13" x2="19" y1="19" y2="13" />
      <line x1="16" x2="20" y1="16" y2="20" />
      <line x1="19" x2="21" y1="21" y2="19" />
      <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 10" />
      <line x1="5" x2="9" y1="14" y2="18" />
      <line x1="7" x2="4" y1="17" y2="20" />
      <line x1="3" x2="5" y1="19" y2="21" />
    </svg>
  );
}

// Catalog ids of every `[Name](card:id)` mention across a row's text lines,
// deduplicated in order of appearance.
function mentionedCardIds(texts) {
  const ids = [];
  for (const t of texts) {
    for (const chunk of parseCardMentions(t || '')) {
      if (chunk.type !== 'text' && chunk.cardId && !ids.includes(chunk.cardId)) ids.push(chunk.cardId);
    }
  }
  return ids;
}

// Feed text combines the logbook's two inline vocabularies: `[Name](card:id)`
// mentions become the same clickable hover-to-preview pills the @-mention
// system renders everywhere else, and bare `[N]` markers become square chips.
function ChronText({ text, onOpenCard, onCardHover, onCardHoverEnd }) {
  const chunks = parseCardMentions(text || '');
  if (chunks.length === 0) return text || null;
  return (
    <>
      {chunks.map((chunk, i) => {
        if (chunk.type === 'text') return <Fragment key={i}>{renderLogTextWithCells(chunk.value)}</Fragment>;
        return (
          <span
            key={`card-${i}-${chunk.cardId}`}
            className="md-card-link"
            data-card-id={chunk.cardId}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // a mention click inspects; it must not seek
              onOpenCard?.(chunk.cardId);
            }}
            onMouseEnter={(e) => onCardHover?.(chunk.cardId, e.currentTarget)}
            onMouseLeave={() => onCardHoverEnd?.()}
          >
            {chunk.name}
          </span>
        );
      })}
    </>
  );
}

// One event row. Its text/seat never change, so a driver tick only re-renders
// the two rows whose isCurrent/isFuture flipped, not the whole feed.
class FeedRow extends Component {
  shouldComponentUpdate(next) {
    return next.isCurrent !== this.props.isCurrent
      || next.isFuture !== this.props.isFuture
      || next.noteBody !== this.props.noteBody;
  }

  render() {
    const { text, seat, cards, avatarSrc, time, noteKey, noteBody, noteCount, isCurrent, isFuture, isCombat, subRows, onSeek, rowRef, onShowFan, onHideFan, onRowHover, onRowHoverEnd, onOpenCard, onCardHover, onCardHoverEnd, onNoteOpen, onNoteHover, onNoteHoverEnd } = this.props;
    const isSystem = !seat;
    // Rows with a thread stay visible at rest, so a scan of the feed shows
    // where the conversation is without hovering every row.
    const restingBg = noteBody ? `${GOLD} 0.07)` : 'transparent';
    return (
      <div
        ref={isCurrent ? rowRef : undefined}
        data-row-key={noteKey}
        className="group relative flex items-baseline cursor-pointer transition-all"
        style={{
          flexWrap: subRows?.length ? 'wrap' : undefined,
          gap: '0.5625cqw',
          margin: '0.125cqw 0.5cqw',
          padding: isSystem ? `0.3125cqw ${time ? '3.5cqw' : '1.75cqw'} 0.3125cqw 0.5cqw` : `0.375cqw ${time ? '3.5cqw' : '1.75cqw'} 0.375cqw 0.5cqw`,
          borderRadius: 'var(--radius-md)',
          fontSize: '0.78cqw',
          lineHeight: 1.45,
          justifyContent: isSystem ? 'center' : undefined,
          background: isCurrent ? `${GOLD} 0.1)` : restingBg,
          // The dim marks "not played yet". On a commented row it would
          // also swallow the marker (opacity on the row can't be undone by
          // a child), so those rows keep enough of themselves to be read.
          opacity: isFuture ? (noteBody ? 0.62 : 0.32) : 1,
          boxShadow: noteBody ? `inset 0.1875cqw 0 0 ${GOLD} 0.55)` : 'none',
        }}
        onMouseEnter={(e) => {
          if (!isCurrent) e.currentTarget.style.background = `${GOLD} 0.05)`;
          if (cards?.length) onShowFan?.(cards, e.currentTarget);
          // Every card the row (and its sub-rows) mentions: the board
          // highlights the cell each one currently stands on.
          const ids = mentionedCardIds([text, ...(subRows || []).map((s) => s.text)]);
          if (ids.length) onRowHover?.(ids);
        }}
        onMouseLeave={(e) => {
          if (!isCurrent) e.currentTarget.style.background = restingBg;
          onHideFan?.();
          onRowHoverEnd?.();
        }}
        onClick={onSeek}
      >
        {!isSystem ? (
          avatarSrc ? (
            <AvatarImage
              src={avatarSrc}
              className="shrink-0 self-center rounded-full"
              style={{
                width: '1.125cqw', height: '1.125cqw',
                border: `1px solid ${SEAT_COLORS[seat]}`,
                boxShadow: '0 1px 3px rgba(0,0,0,0.6)',
              }}
            />
          ) : (
            <span className="shrink-0 self-center rounded-full" style={{ width: '0.3125cqw', height: '0.3125cqw', background: SEAT_COLORS[seat] }} />
          )
        ) : null}
        {isCombat ? <CombatIcon /> : null}
        <span style={{ color: isSystem ? `${GOLD} 0.55)` : (isCurrent ? TEXT_PRIMARY : TEXT_BODY), fontSize: isSystem ? '0.72cqw' : undefined }}>
          <ChronText text={text} onOpenCard={onOpenCard} onCardHover={onCardHover} onCardHoverEnd={onCardHoverEnd} />
        </span>
        {subRows?.length ? (
          <div style={{ width: '100%', paddingLeft: '1.6875cqw', display: 'flex', flexDirection: 'column', gap: '0.1875cqw', marginTop: '0.1875cqw' }}>
            {subRows.map((sub, i) => (
              <div key={i} className="flex items-baseline" style={{ gap: '0.4375cqw', fontSize: '0.72cqw', lineHeight: 1.4, color: TEXT_MUTED }}>
                <span
                  className="shrink-0 self-center rounded-full"
                  style={{ width: '0.25cqw', height: '0.25cqw', background: sub.seat ? SEAT_COLORS[sub.seat] : `${GOLD} 0.45)` }}
                />
                <span>
                  <ChronText text={sub.text} onOpenCard={onOpenCard} onCardHover={onCardHover} onCardHoverEnd={onCardHoverEnd} />
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {time ? (
          <span
            className="absolute tabular-nums"
            style={{
              right: '1.75cqw', top: subRows?.length ? '0.6cqw' : '50%', transform: subRows?.length ? 'none' : 'translateY(-50%)',
              fontSize: '0.625cqw', color: TEXT_MUTED,
            }}
          >
            {time}
          </span>
        ) : null}
        <button
          type="button"
          title={noteBody ? t('View comments') : t('Add a comment')}
          className={`absolute flex items-center justify-center cursor-pointer transition-opacity ${noteBody ? 'opacity-100' : 'opacity-0 group-hover:opacity-50 hover:!opacity-90'}`}
          style={{
            right: '0.25cqw',
            top: subRows?.length ? '0.3125cqw' : '50%',
            transform: subRows?.length ? 'none' : 'translateY(-50%)',
            width: '1.5cqw', height: '1.5cqw', borderRadius: 'var(--radius-md)',
            background: 'transparent', border: 'none',
            color: noteBody ? ACCENT_GOLD : TEXT_MUTED,
          }}
          onClick={(e) => { e.stopPropagation(); onNoteOpen?.(noteKey, e.currentTarget); }}
          onMouseEnter={(e) => { if (noteBody) onNoteHover?.(noteKey, e.currentTarget); }}
          onMouseLeave={() => onNoteHoverEnd?.()}
        >
          <NoteIcon filled={!!noteBody} />
          {noteCount > 1 ? (
            <span
              className="absolute tabular-nums"
              style={{
                top: '-0.0625cqw', right: '-0.0625cqw',
                fontSize: '0.55cqw', fontWeight: 700, lineHeight: 1,
                color: '#0e0a06', background: ACCENT_GOLD,
                borderRadius: '50%', padding: '0.0625cqw 0.1875cqw',
              }}
            >
              {noteCount}
            </span>
          ) : null}
        </button>
      </div>
    );
  }
}

// The viewer's own name for the match, editable in place. A participant
// names a match from here as from the lists; a visitor holds no row for
// it, so they see the name only if the host hands one over, and no field.
class MatchNameField extends Component {
  state = { draft: null };

  commit = () => {
    const { draft } = this.state;
    if (draft !== null && draft.trim() !== (this.props.name || '')) this.props.onRename(draft);
    this.setState({ draft: null });
  };

  render() {
    const { name, onRename } = this.props;
    const style = { fontSize: '0.875cqw', color: TEXT_PRIMARY, marginTop: '0.625cqw' };
    if (!onRename) {
      return name ? <div className="text-center truncate font-semibold" style={style}>{name}</div> : null;
    }
    return (
      <input
        type="text"
        aria-label={t('Match name')}
        value={this.state.draft ?? name ?? ''}
        placeholder={t('Name this match')}
        maxLength={60}
        className="w-full text-center font-semibold outline-none transition-colors"
        style={{
          ...style,
          padding: '0.25cqw 0.5cqw',
          background: 'transparent',
          border: `1px solid ${GOLD} 0.15)`,
          borderRadius: 'var(--radius-md)',
        }}
        onFocus={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.45)`; e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; }}
        onInput={(e) => this.setState({ draft: e.currentTarget.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { this._discard = true; e.currentTarget.blur(); }
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = `${GOLD} 0.15)`;
          e.currentTarget.style.background = 'transparent';
          if (this._discard) { this._discard = false; this.setState({ draft: null }); } else this.commit();
        }}
      />
    );
  }
}

function TransportBtn({ title, onClick, d }) {
  return (
    <button
      type="button"
      title={title}
      className="flex items-center justify-center cursor-pointer transition-colors rounded-lg"
      style={{ width: '2cqw', height: '2cqw', background: 'transparent', border: 'none', color: TEXT_BODY }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.1)`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      onClick={onClick}
    >
      <svg style={{ width: '1cqw', height: '1cqw' }} viewBox="0 0 24 24" fill="currentColor"><path d={d} /></svg>
    </button>
  );
}

export default class ReplayOverlay extends Component {
  constructor(props) {
    super(props);
    this.rows = buildRows(props.view);
    this.turnSeqs = this.rows.filter((r) => r.type === 'turn').map((r) => r.seq);
    // Card art lookup for the draw-row hover fan.
    this.cardArt = new Map((props.sorceryCards || [])
      .map((c) => [c.unique_id, c.printings?.[0]?.image_url || null]));
    // Each seat's Avatar portrait, shown on every feed row the seat acts in.
    const avatars = props.view.meta?.avatars || {};
    this.seatAvatars = {
      p1: avatars.p1?.cardId ? this.cardArt.get(avatars.p1.cardId) : null,
      p2: avatars.p2?.cardId ? this.cardArt.get(avatars.p2.cardId) : null,
    };
    this.state = {
      status: props.driver.getStatus(),
      follow: true,
      // Viewpoint swap veil: null | 'in' (board fading to black) | 'out'
      // (fading back up over the flipped seat). See beginViewpointSwap.
      veil: null,
      fan: null,
      notePanel: null,
      draft: '',
      posting: false,
      hoveredCommentId: null,
      // Comment a notification deep-linked to; the gold wash fades on a timer.
      highlightCommentId: null,
      likeBusy: null,
      shareOpen: false,
      shareQuery: '',
    };
    this.feedRef = null;
    this.threadListRef = null;
    this.timelineRef = null;
    this._highlightTimer = null;
    this._currentRowEl = null;
    this._scrubbing = false;
    this._scrubFrac = null;
    this._scrubRaf = null;
  }

  componentDidMount() {
    this._unsub = this.props.driver.onChange((status) => {
      this.setState({ status }, () => {
        if (this.state.follow) this._centerCurrentRow();
      });
    });
    window.addEventListener('keydown', this._onKeyDown, true);
    if (this.props.initialNoteKey) this._jumpToNote(this.props.initialNoteKey, this.props.highlightCommentId);
  }

  componentDidUpdate(prevProps) {
    const key = this.props.initialNoteKey;
    if (key && key !== prevProps.initialNoteKey) this._jumpToNote(key, this.props.highlightCommentId);
  }

  componentWillUnmount() {
    this._unmounted = true;
    this._unsub?.();
    window.removeEventListener('keydown', this._onKeyDown, true);
    cancelAnimationFrame(this._scrubRaf);
    clearTimeout(this._highlightTimer);
  }

  // Every transport action re-engages follow mode: the user asked to move the
  // playhead, so the feed should track it again.
  _drive = (fn) => {
    this.setState({ follow: true });
    fn();
  };

  _onKeyDown = (e) => {
    // While a comment is being written the keys belong to the composer; only
    // Escape (close the panel) is intercepted.
    if (this.state.notePanel?.pinned) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._closeNote();
      }
      return;
    }
    // A field being typed in (the match name, the share search) owns its keys.
    if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
    // Capture-phase so the board's own hotkeys never race the transport keys.
    const handled = e.code === 'Space' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';
    if (!handled) return;
    e.preventDefault();
    e.stopPropagation();
    const { driver } = this.props;
    if (e.code === 'Space') {
      if (driver.playing) driver.pause();
      else this._drive(() => driver.play());
    } else if (e.key === 'ArrowRight') {
      this._drive(() => (e.shiftKey ? this._jumpTurn(1) : driver.stepForward()));
    } else if (e.key === 'ArrowLeft') {
      this._drive(() => (e.shiftKey ? this._jumpTurn(-1) : driver.stepBack()));
    }
  };

  _jumpTurn(direction) {
    const { driver } = this.props;
    const { index } = driver.getStatus();
    if (direction > 0) {
      const next = this.turnSeqs.find((s) => s > index);
      driver.seek(next ?? driver.frameCount());
    } else {
      // Back to the start of the current turn; a second press crosses into
      // the previous one (the standard prev-chapter behavior).
      const at = [...this.turnSeqs].reverse().find((s) => s < index);
      driver.seek(at ?? 0);
    }
  }

  _centerCurrentRow() {
    this._centerIn(this.feedRef, this._currentRowEl);
  }

  _centerIn(scroller, el) {
    if (!scroller || !el || typeof scroller.scrollTo !== 'function') return;
    const target = el.offsetTop - scroller.clientHeight / 2 + el.offsetHeight / 2;
    scroller.scrollTo({ top: target, behavior: 'smooth' });
  }

  _onFeedWheel = () => {
    if (this.state.follow) this.setState({ follow: false });
  };

  // Draw-row hover fan: the drawn cards spread out beside the sidebar at the
  // row's height. Display-only (pointer-events none), so it never traps the
  // cursor on its way back to the feed.
  _showFan = (cards, element) => {
    const rect = rectToCanvasRect(element.getBoundingClientRect());
    this.setState({ fan: { cards, top: Math.round(rect.top) } });
  };

  _hideFan = () => {
    if (this.state.fan) this.setState({ fan: null });
  };

  // ── Row comments (a comment thread per feed row) ─────────────────
  _panelTopFor(element) {
    return Math.round(rectToCanvasRect(element.getBoundingClientRect()).top);
  }

  _thread(key) {
    return this.props.comments?.[key] || [];
  }

  _openNote = (key, element) => {
    playUI(UI.CLICK);
    this.setState({ notePanel: { key, top: this._panelTopFor(element), pinned: true }, draft: '', fan: null });
  };

  // Notification deep link: land the playhead on the row's frame, bring the
  // row into view, and pin its thread with the linked comment lit up. Rows
  // are keyed `seq:idx` / `seq:turn` (`0:turn` is the prelude); every row is
  // always in the DOM, so the lookup needs no render round-trip. Follow mode
  // is switched off first so the driver tick does not fight the scroll.
  _jumpToNote(key, commentId) {
    const seq = parseInt(key, 10);
    if (!Number.isFinite(seq)) return;
    this.setState({ follow: false });
    this.props.driver.seek(seq);
    const rowEl = this.feedRef?.querySelector(`[data-row-key="${key}"]`) || null;
    if (rowEl) this._centerIn(this.feedRef, rowEl);
    const top = rowEl
      ? this._panelTopFor(rowEl)
      : Math.round((document.querySelector('.app-canvas')?.clientHeight || window.innerHeight) * 0.3);
    clearTimeout(this._highlightTimer);
    this._highlightTimer = null;
    const highlightCommentId = commentId || null;
    this.setState({ notePanel: { key, top, pinned: true }, draft: '', fan: null, highlightCommentId }, () => {
      if (!highlightCommentId) return;
      const el = this.threadListRef?.querySelector(`[data-comment-id="${highlightCommentId}"]`);
      if (el) this._centerIn(this.threadListRef, el);
      this._highlightTimer = setTimeout(() => {
        this._highlightTimer = null;
        if (!this._unmounted) this.setState({ highlightCommentId: null });
      }, 3500);
    });
  }

  _peekNote = (key, element) => {
    if (this.state.notePanel?.pinned) return;
    this.setState({ notePanel: { key, top: this._panelTopFor(element), pinned: false } });
  };

  _peekEnd = () => {
    if (this.state.notePanel && !this.state.notePanel.pinned) this.setState({ notePanel: null });
  };

  _closeNote = () => {
    this.setState({ notePanel: null, draft: '' });
  };

  _postComment = async (key) => {
    const body = this.state.draft.trim();
    if (!body || this.state.posting) return;
    this.setState({ posting: true });
    try {
      await this.props.onPostComment?.(key, body);
      this.setState({ draft: '', posting: false });
    } catch {
      this.setState({ posting: false });
    }
  };

  _toggleLike = async (key, commentId) => {
    if (this.state.likeBusy) return;
    this.setState({ likeBusy: commentId });
    try {
      await this.props.onToggleCommentLike?.(key, commentId);
    } finally {
      this.setState({ likeBusy: null });
    }
  };

  // Scrub drags coalesce to one seek per animation frame: raw pointermove
  // fires far faster than the board can rebuild from a Full.
  _scrubTo = (e) => {
    const rect = this.timelineRef.getBoundingClientRect();
    this._scrubFrac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (this._scrubRaf) return;
    this._scrubRaf = requestAnimationFrame(() => {
      this._scrubRaf = null;
      this.props.driver.seek(Math.round(this._scrubFrac * this.props.driver.frameCount()));
    });
  };

  _name(seat) {
    return this.props.names?.[seat] || (seat === 'p1' ? t('Player 1') : t('Player 2'));
  }

  // A row is a message (`{ key, values }`) rendered in the viewer's language;
  // a text-only row (a replay recorded before messages) renders as stored.
  // Seat placeholders are filled after translation, so names never translate.
  _text(row) {
    const text = typeof row.key === 'string' ? t(row.key, row.values) : (row.text || '');
    return text.replace(/\{(p1|p2)\}/g, (_, seat) => this._name(seat));
  }

  // Circular avatar-card portrait + name, one per seat. The art is each
  // seat's Avatar card (always known, even for opponents), cropped to the
  // face region the way profile avatars are cropped elsewhere.
  // Share dropdown: copy a public link, or pick a friend (filtered by the
  // search box) to send the replay to as a chat attachment.
  _renderShareMenu() {
    const q = this.state.shareQuery.trim().toLowerCase();
    const friends = (this.props.friends || [])
      .filter((f) => !q || (f.name || '').toLowerCase().includes(q))
      .slice(0, 8);
    const close = () => this.setState({ shareOpen: false, shareQuery: '' });
    return (
      <>
        <div className="fixed inset-0" style={{ zIndex: 19 }} onClick={close} />
        <PopoverPanel style={{ position: 'absolute', top: '3cqw', right: '0.875cqw', width: '14cqw', zIndex: 20 }}>
          <PopoverMenuItem
            label={t('Copy link')}
            caption={t('Anyone with the link can watch')}
            leading={<IconLink size={14} />}
            onClick={() => { close(); this.props.onCopyLink?.(); }}
          />
          {this.props.onShareToTavern ? (
            <PopoverMenuItem
              label={t('Post to the Tavern')}
              caption={t('Everyone in the Tavern can watch')}
              leading={<TavernIcon />}
              onClick={() => { close(); this.props.onShareToTavern(); }}
            />
          ) : null}
          <div style={{ height: 1, background: `${GOLD} 0.15)`, margin: '0.25cqw 0.5cqw' }} />
          <input
            type="text"
            value={this.state.shareQuery}
            placeholder={t('Send to a friend...')}
            autoFocus
            onInput={(e) => this.setState({ shareQuery: e.currentTarget.value })}
            className="w-full outline-none text-xs"
            style={{ padding: '0.4cqw 0.75cqw', background: 'transparent', color: TEXT_PRIMARY, border: 'none' }}
          />
          {friends.length === 0 ? (
            <div className="text-xs italic" style={{ color: TEXT_MUTED, padding: '0.35cqw 0.75cqw 0.5cqw' }}>
              {q ? t('No friend matches') : t('No friends yet')}
            </div>
          ) : friends.map((f) => {
            const url = resolveAvatarUrl(f, this.props.sorceryCards);
            return (
              <PopoverMenuItem
                key={f.id}
                label={f.name}
                leading={url
                  ? <img src={url} alt="" style={{ width: '1.25cqw', height: '1.25cqw', borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ width: '1.25cqw', height: '1.25cqw', borderRadius: '50%', background: `${GOLD} 0.2)`, display: 'inline-block' }} />}
                onClick={() => { close(); this.props.onShareToFriend?.(f); }}
              />
            );
          })}
        </PopoverPanel>
      </>
    );
  }

  _playerBadge(seat) {
    const avatar = this.props.view.meta?.avatars?.[seat];
    const src = avatar?.cardId ? this.cardArt.get(avatar.cardId) : null;
    return (
      <div className="flex flex-col items-center min-w-0" style={{ width: '7.75cqw' }}>
        <div
          className="rounded-full overflow-hidden shrink-0"
          style={{
            width: '3.75cqw', height: '3.75cqw',
            border: `1px solid ${GOLD} 0.5)`,
            boxShadow: '0 4px 14px rgba(0,0,0,0.6), 0 0 0 2px rgba(0,0,0,0.55)',
          }}
        >
          {src ? (
            <AvatarImage src={src} className="w-full h-full" />
          ) : (
            <div className="w-full h-full" style={{ background: 'rgba(0,0,0,0.45)' }} />
          )}
        </div>
        <div
          className="truncate max-w-full"
          style={{ fontSize: '0.8125cqw', marginTop: '0.375cqw', color: SEAT_COLORS[seat], textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}
        >
          {this._name(seat)}
        </div>
      </div>
    );
  }

  // The seat flip itself is instant (replayViewpoint controller), so on its
  // own it reads as a hard cut. Wrap it in a veil: the board fades to black
  // under the sidebar, the seat flips at full black, and the board fades back
  // up once the re-fold has painted. The sidebar stays put throughout; it is
  // the same chronicle either way.
  beginViewpointSwap = () => {
    if (this.state.veil) return;
    playUI(UI.CLICK);
    this.setState({ veil: 'in' });
  };

  handleVeilComplete = () => {
    if (this._unmounted) return;
    const { veil } = this.state;
    if (veil === 'in') {
      const { viewpoint, onSetViewpoint } = this.props;
      onSetViewpoint?.(viewpoint === 'p2' ? 'p1' : 'p2');
      // Two frames: one for the seat flip to commit, one for the Full
      // re-fold to paint, so the fade-up never reveals the old side.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!this._unmounted) this.setState({ veil: 'out' });
      }));
    } else if (veil === 'out') {
      this.setState({ veil: null });
    }
  };

  render() {
    const { driver, view, viewpoint, onExit } = this.props;
    const { status, follow } = this.state;
    const otherSeat = viewpoint === 'p2' ? 'p1' : 'p2';
    const total = driver.frameCount();
    const frac = total > 0 ? status.index / total : 0;

    // The row the playhead sits on: the last row whose seq <= index.
    let currentRowIdx = -1;
    for (let i = 0; i < this.rows.length; i++) {
      if (this.rows[i].seq <= status.index) currentRowIdx = i;
    }

    return (
      <div className="fixed inset-0 z-[80] pointer-events-none">
        {/* ── Viewpoint veil: under the sidebar (DOM order), over the board. ── */}
        <motion.div
          aria-hidden
          data-testid="viewpoint-veil"
          className="absolute inset-0"
          style={{ background: '#050403', opacity: 0 }}
          initial={false}
          animate={{ opacity: this.state.veil === 'in' ? 1 : 0 }}
          transition={this.state.veil === 'in'
            ? { duration: 0.18, ease: 'easeOut' }
            : { duration: 0.36, ease: 'easeIn' }}
          onAnimationComplete={this.handleVeilComplete}
        />
        {/* ── Chronicle sidebar ─────────────────────────────── */}
        <aside
          className="absolute flex flex-col pointer-events-auto overflow-hidden"
          style={{
            top: '0.75cqw', left: '0.75cqw', bottom: '0.75cqw',
            width: SIDEBAR_WIDTH,
            background: 'linear-gradient(180deg, rgba(15,12,8,0.92) 0%, rgba(9,7,5,0.94) 100%)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${GOLD} 0.25)`,
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 14px 44px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}
        >
          {/* ── Header: the matchup. Circular Avatar portraits with the
              names beneath, and the ranked-tile VS mark between them. ── */}
          <div className="relative shrink-0" style={{ padding: '1cqw 1cqw 0.875cqw', borderBottom: `1px solid ${GOLD} 0.12)` }}>
            <button
              type="button"
              title={t('Leave replay')}
              className="absolute flex items-center justify-center cursor-pointer transition-colors"
              style={{
                top: '0.875cqw', left: '0.875cqw',
                width: '1.875cqw', height: '1.875cqw', borderRadius: 'var(--radius-md)',
                background: 'transparent', border: `1px solid ${GOLD} 0.22)`, color: TEXT_BODY,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; e.currentTarget.style.borderColor = `${GOLD} 0.4)`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = `${GOLD} 0.22)`; }}
              onClick={() => { playUI(UI.CANCEL); onExit?.(); }}
            >
              <svg style={{ width: '0.875cqw', height: '0.875cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 3 5 8l5 5" /></svg>
            </button>
            {this.props.canShare ? (
              <button
                type="button"
                title={t('Share replay')}
                className="absolute flex items-center justify-center cursor-pointer transition-colors"
                style={{
                  top: '0.875cqw', right: '0.875cqw',
                  width: '1.875cqw', height: '1.875cqw', borderRadius: 'var(--radius-md)',
                  background: this.state.shareOpen ? `${GOLD} 0.12)` : 'transparent',
                  border: `1px solid ${GOLD} ${this.state.shareOpen ? '0.4' : '0.22'})`, color: TEXT_BODY,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; e.currentTarget.style.borderColor = `${GOLD} 0.4)`; }}
                onMouseLeave={(e) => { if (!this.state.shareOpen) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = `${GOLD} 0.22)`; } }}
                onClick={() => { playUI(UI.CLICK); this.setState((st) => ({ shareOpen: !st.shareOpen, shareQuery: '' })); }}
              >
                <ShareIcon />
              </button>
            ) : null}
            {this.state.shareOpen ? this._renderShareMenu() : null}
            <div className="flex items-start justify-center" style={{ gap: '0.75cqw' }}>
              {this._playerBadge('p1')}
              {/* Same face as the Friend Match hero's fighting-game VS. */}
              <span
                className="arena-heading font-bold shrink-0"
                style={{
                  color: '#fff6d5',
                  fontSize: '1.5cqw',
                  letterSpacing: '0.04em',
                  fontStyle: 'italic',
                  WebkitTextStroke: `0.0625cqw ${ACCENT_GOLD}`,
                  marginTop: '1cqw',
                  textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                }}
              >
                VS
              </span>
              {this._playerBadge('p2')}
            </div>
            <MatchNameField name={this.props.matchName} onRename={this.props.onRenameMatch} />
            <div className="text-center" style={{ fontSize: '0.6875cqw', color: TEXT_MUTED, marginTop: '0.5cqw' }}>
              {[view.meta?.mode, view.meta?.endedAt ? new Date(view.meta.endedAt).toLocaleDateString() : null].filter(Boolean).join(' · ') || t('Replay')}
            </div>
          </div>

          <div className="relative flex-1 min-h-0">
          <div
            className="h-full overflow-y-auto"
            style={{ scrollbarWidth: 'none' }}
            ref={(el) => { this.feedRef = el; }}
            onWheel={this._onFeedWheel}
          >
            {this.rows.map((row, i) => {
              if (row.type === 'turn') {
                const headerNote = this._thread(row.noteKey).length > 0;
                const headerTime = formatClock(row.t);
                return (
                  <div
                    key={`t${i}`}
                    data-row-key={row.noteKey}
                    className="group sticky top-0 z-[5] flex items-baseline gap-2 relative"
                    style={{
                      padding: '0.5cqw 1.75cqw 0.4375cqw 1cqw',
                      background: 'linear-gradient(180deg, rgba(22,17,10,0.98), rgba(14,11,7,0.98))',
                      borderBottom: `1px solid ${GOLD} 0.12)`,
                    }}
                  >
                    <span className="arena-heading" style={{ fontSize: '0.8125cqw', letterSpacing: '0.06em', color: `${GOLD} 0.75)` }}>
                      {row.n === 0 ? t('Prelude') : t('Turn {n}', { n: row.n })}
                    </span>
                    {row.seat ? (
                      <span className="flex items-center self-center" style={{ gap: '0.3125cqw' }}>
                        {this.seatAvatars[row.seat] ? (
                          <AvatarImage
                            src={this.seatAvatars[row.seat]}
                            className="rounded-full shrink-0"
                            style={{ width: '1cqw', height: '1cqw', border: `1px solid ${SEAT_COLORS[row.seat]}` }}
                          />
                        ) : null}
                        <span style={{ fontSize: '0.6875cqw', color: SEAT_COLORS[row.seat] }}>{this._name(row.seat)}</span>
                      </span>
                    ) : null}
                    <div className="flex-1 self-center" style={{ height: '1px', background: `linear-gradient(90deg, ${GOLD} 0.18), transparent)` }} />
                    {headerTime ? (
                      <span className="self-center tabular-nums shrink-0" style={{ fontSize: '0.625cqw', color: TEXT_MUTED, marginRight: '0.25cqw' }}>{headerTime}</span>
                    ) : null}
                    <button
                      type="button"
                      title={headerNote ? t('View comments') : t('Comment on this turn')}
                      className={`absolute flex items-center justify-center cursor-pointer transition-opacity ${headerNote ? 'opacity-100' : 'opacity-0 group-hover:opacity-50 hover:!opacity-90'}`}
                      style={{
                        right: '0.375cqw', top: '50%', transform: 'translateY(-50%)',
                        width: '1.5cqw', height: '1.5cqw', borderRadius: 'var(--radius-md)',
                        background: 'transparent', border: 'none',
                        color: headerNote ? ACCENT_GOLD : TEXT_MUTED,
                      }}
                      onClick={(e) => { e.stopPropagation(); this._openNote(row.noteKey, e.currentTarget); }}
                      onMouseEnter={(e) => { if (headerNote) this._peekNote(row.noteKey, e.currentTarget); }}
                      onMouseLeave={this._peekEnd}
                    >
                      <NoteIcon filled={!!headerNote} />
                    </button>
                  </div>
                );
              }
              return (
                <FeedRow
                  key={`e${i}`}
                  text={this._text(row)}
                  seat={row.seat}
                  avatarSrc={row.seat ? this.seatAvatars[row.seat] : null}
                  cards={row.cards}
                  isCurrent={i === currentRowIdx}
                  isFuture={row.seq > status.index}
                  isCombat={row.kind === 'combat'}
                  subRows={row.children?.map((c) => ({ ...c, text: this._text(c) }))}
                  rowRef={(el) => { this._currentRowEl = el; }}
                  onSeek={() => this._drive(() => driver.seek(row.seq))}
                  onShowFan={this._showFan}
                  onHideFan={this._hideFan}
                  onRowHover={this.props.onRowHover}
                  onRowHoverEnd={this.props.onRowHoverEnd}
                  onOpenCard={this.props.onOpenCard}
                  onCardHover={this.props.onCardHover}
                  onCardHoverEnd={this.props.onCardHoverEnd}
                  time={formatClock(row.t)}
                  noteKey={row.noteKey}
                  noteBody={this._thread(row.noteKey).map((c) => `${c.id}:${c.body}:${c.likes}`).join('\u0000') || null}
                  noteCount={this._thread(row.noteKey).length}
                  onNoteOpen={this._openNote}
                  onNoteHover={this._peekNote}
                  onNoteHoverEnd={this._peekEnd}
                />
              );
            })}
            <div style={{ height: '30%' }} />
          </div>

          {!follow ? (
            <button
              type="button"
              className="absolute z-[6] flex items-center gap-2 cursor-pointer"
              style={{
                left: '50%', bottom: '0.625cqw', transform: 'translateX(-50%)',
                padding: '0.4375cqw 0.875cqw', borderRadius: 'var(--radius-full)',
                background: 'rgba(18,14,9,0.95)', border: `1px solid ${GOLD} 0.45)`,
                color: TEXT_PRIMARY, fontSize: '0.75cqw',
                boxShadow: '0 6px 20px rgba(0,0,0,0.55)',
              }}
              onClick={() => this.setState({ follow: true }, () => this._centerCurrentRow())}
            >
              <svg style={{ width: '0.75cqw', height: '0.75cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v10M4 9l4 4 4-4" /></svg>
              {t('Return to now')}
            </button>
          ) : null}
          </div>

          {/* ── Footer: the transport + scrubber. In the sidebar, never
              floating over the board, so the hand is never obscured. ── */}
          <div className="shrink-0" style={{ padding: '0.75cqw 1cqw 0.875cqw', borderTop: `1px solid ${GOLD} 0.12)`, background: 'rgba(0,0,0,0.25)' }}>
            <div
              className="relative cursor-pointer"
              style={{ height: '1.25cqw', touchAction: 'none' }}
              ref={(el) => { this.timelineRef = el; }}
              onPointerDown={(e) => {
                this._scrubbing = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                driver.pause();
                this.setState({ follow: true });
                this._scrubTo(e);
              }}
              onPointerMove={(e) => { if (this._scrubbing) this._scrubTo(e); }}
              onPointerUp={() => { this._scrubbing = false; }}
            >
              <div className="absolute left-0 right-0 rounded-full" style={{ top: '0.5cqw', height: '3px', background: 'rgba(255,255,255,0.09)' }} />
              <div className="absolute left-0 rounded-full" style={{ top: '0.5cqw', height: '3px', width: `${frac * 100}%`, background: ACCENT_GOLD }} />
              {this.turnSeqs.filter((s) => s > 0).map((s) => (
                <div key={s} className="absolute" style={{ left: `${(s / Math.max(1, total)) * 100}%`, top: '0.375cqw', width: '1px', height: '0.4375cqw', background: `${GOLD} 0.35)`, transform: 'translateX(-50%)' }} />
              ))}
              <div
                className="absolute rounded-full"
                style={{
                  left: `${frac * 100}%`, top: 'calc(0.5cqw + 1.5px)', width: '0.6875cqw', height: '0.6875cqw',
                  transform: 'translate(-50%, -50%)',
                  background: `radial-gradient(circle at 35% 30%, #f2d896, ${ACCENT_GOLD} 55%, #8a6526)`,
                  border: '1px solid rgba(255,230,160,0.6)', boxShadow: '0 1px 4px rgba(0,0,0,0.7)',
                }}
              />
            </div>

            <div className="flex items-center" style={{ marginTop: '0.375cqw' }}>
              <div className="shrink-0" style={{ width: '3.75cqw', fontSize: '0.6875cqw', color: TEXT_MUTED }}>
                {status.index} / {total}
              </div>
              <div className="flex items-center mx-auto" style={{ gap: '0.125cqw' }}>
                <TransportBtn title={t('Previous turn')} d="M6 5h2v14H6zM20 5 9.5 12 20 19z" onClick={() => this._drive(() => this._jumpTurn(-1))} />
                <TransportBtn title={t('Step back')} d="M19.5 5 9 12l10.5 7zM7.5 5h2v14h-2z" onClick={() => this._drive(() => driver.stepBack())} />
                <button
                  type="button"
                  title={status.playing ? t('Pause') : t('Play')}
                  className="flex items-center justify-center cursor-pointer rounded-full"
                  style={{
                    ...GOLD_BTN,
                    width: '2.25cqw', height: '2.25cqw', margin: '0 0.25cqw', borderRadius: '50%',
                  }}
                  onClick={() => {
                    playUI(UI.CLICK);
                    if (status.playing) driver.pause();
                    else this._drive(() => driver.play());
                  }}
                >
                  <svg style={{ width: '0.9375cqw', height: '0.9375cqw' }} viewBox="0 0 24 24" fill="currentColor">
                    {status.playing ? <path d="M7 5h3.4v14H7zM13.6 5H17v14h-3.6z" /> : <path d="M8 5.5 19 12 8 18.5z" />}
                  </svg>
                </button>
                <TransportBtn title={t('Step forward')} d="M4.5 5 15 12 4.5 19zM14.5 5h2v14h-2z" onClick={() => this._drive(() => driver.stepForward())} />
                <TransportBtn title={t('Next turn')} d="M16 5h2v14h-2zM4 5l10.5 7L4 19z" onClick={() => this._drive(() => this._jumpTurn(1))} />
              </div>
              <div className="flex justify-end shrink-0" style={{ width: '3.75cqw' }}>
                <button
                  type="button"
                  title={t('Playback speed')}
                  className="cursor-pointer transition-colors text-center"
                  style={{
                    minWidth: '2.25cqw', padding: '0.3125cqw 0', borderRadius: 'var(--radius-md)',
                    background: 'transparent', border: `1px solid ${GOLD} 0.2)`,
                    color: TEXT_BODY, fontSize: '0.6875cqw',
                  }}
                  onClick={() => {
                    const next = SPEEDS[(SPEEDS.indexOf(status.speed) + 1) % SPEEDS.length];
                    driver.setSpeed(next);
                  }}
                >
                  {status.speed}&times;
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* ── Draw hover fan: the drawn cards, spread beside the sidebar ── */}
        {this.state.fan ? (
          <div
            className="absolute flex items-end pointer-events-none"
            style={{ left: `calc(0.75cqw + ${SIDEBAR_WIDTH} + 0.625cqw)`, top: `min(${this.state.fan.top}px, calc(100% - 9.5cqw))`, zIndex: 7 }}
          >
            {this.state.fan.cards.map((card, i) => {
              const src = this.cardArt.get(card.cardId);
              const mid = (this.state.fan.cards.length - 1) / 2;
              return (
                <img
                  key={`${card.cardId}-${i}`}
                  src={src || '/cardback-spellbook-rounded.png'}
                  alt={card.name}
                  className="rounded-lg"
                  style={{
                    width: '6cqw',
                    marginLeft: i ? '-3cqw' : 0,
                    transform: `rotate(${(i - mid) * 4}deg) translateY(${Math.abs(i - mid) * 0.35}cqw)`,
                    boxShadow: '0 10px 26px rgba(0,0,0,0.65)',
                    border: '1px solid rgba(0,0,0,0.5)',
                  }}
                />
              );
            })}
          </div>
        ) : null}

        {/* ── Row comment thread: peek on hover, pinned on click. The same
            comment surface the deck notes use. Every author's comments read
            as forum rows; pinning adds the composer at the bottom. ── */}
        {this.state.notePanel ? (() => {
          const { key, top, pinned } = this.state.notePanel;
          const panelTop = `min(${top}px, calc(100% - 24cqw))`;
          const thread = this._thread(key);
          return (
            <>
              {pinned ? (
                <div
                  className="absolute inset-0 pointer-events-auto"
                  onClick={this._closeNote}
                  onWheel={this._closeNote}
                />
              ) : null}
              <div
                className={`absolute flex flex-col ${pinned ? 'pointer-events-auto' : 'pointer-events-none'}`}
                style={{
                  left: `calc(0.75cqw + ${SIDEBAR_WIDTH} + 0.625cqw)`,
                  top: panelTop,
                  // Bottom-bounded: the composer grows with the draft, and
                  // the list gives up height before the panel leaves the
                  // canvas.
                  maxHeight: `calc(100% - ${panelTop} - 0.75cqw)`,
                  width: '21cqw',
                  padding: '0.5cqw 0 0.625cqw',
                  background: 'linear-gradient(180deg, rgba(21,17,11,0.98), rgba(13,10,6,0.98))',
                  border: `1px solid ${GOLD} 0.3)`,
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: '0 10px 32px rgba(0,0,0,0.55)',
                  zIndex: 9,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Fixed list height, always. A thread panel that sized to
                    its content jumped between a two-line box and a tall one
                    as the cursor moved down the feed, and the composer moved
                    with it. Overflow scrolls (the app-wide overlay thumb
                    picks it up on its own). */}
                <div style={{ height: '16cqw', overflowY: 'auto', paddingTop: '1cqw' }} ref={(el) => { this.threadListRef = el; }}>
                  {thread.length === 0 ? (
                    <div
                      className="flex items-center justify-center text-xs text-center italic h-full"
                      style={{ color: TEXT_MUTED, padding: '0 0.85cqw' }}
                    >
                      {this.props.canComment !== false ? t('No comments yet. Be the first to share a thought.') : t('No comments yet.')}
                    </div>
                  ) : thread.map((c, idx) => (
                    <Fragment key={c.id}>
                      <CommentRow
                        comment={c}
                        continued={continuesPrevious(thread[idx - 1], c)}
                        avatarUrl={this.cardArt.get(c.playerAvatar) || null}
                        sorceryCards={this.props.sorceryCards}
                        canDelete={c.playerId === this.props.selfId}
                        isHovered={this.state.hoveredCommentId === c.id}
                        highlighted={c.id === this.state.highlightCommentId}
                        onHoverChange={(id) => this.setState((st) => (
                          id === null ? (st.hoveredCommentId === c.id ? { hoveredCommentId: null } : null) : { hoveredCommentId: id }
                        ))}
                        likeBusy={this.state.likeBusy === c.id}
                        onOpenProfile={this.props.onOpenProfile}
                        onDelete={(id) => this.props.onDeleteComment?.(key, id)}
                        onToggleLike={this.props.canComment !== false ? (id) => this._toggleLike(key, id) : undefined}
                        onCardClick={this.props.onOpenCard}
                        onCardHover={this.props.onCardHover}
                        onCardHoverEnd={this.props.onCardHoverEnd}
                      />
                    </Fragment>
                  ))}
                </div>
                {this.props.canComment === false ? (
                  <div className="flex items-center justify-between" style={{ padding: '0.5cqw 0.75cqw 0', gap: '0.5cqw' }}>
                    <span className="text-xs" style={{ color: TEXT_MUTED }}>{t('Sign in with your Valkenhall account to comment.')}</span>
                    <button
                      type="button"
                      className="text-xs font-semibold shrink-0 cursor-pointer"
                      style={{ padding: '0.3cqw 0.75cqw', borderRadius: 'var(--radius-md)', background: `${GOLD} 0.14)`, border: `1px solid ${GOLD} 0.4)`, color: TEXT_PRIMARY }}
                      onClick={() => { playUI(UI.CLICK); this.props.onRequestLogin?.(); }}
                    >
                      {t('Sign in')}
                    </button>
                  </div>
                ) : (
                <div className="shrink-0" style={{ padding: '0.5cqw 0.625cqw 0' }}>
                  <CommentComposer
                    // Remount on pin so the composer takes focus; a prop
                    // flip alone would leave the caret where it was.
                    key={pinned ? 'pinned' : 'peek'}
                    value={this.state.draft}
                    onChange={(v) => this.setState({ draft: v })}
                    onSubmit={() => this._postComment(key)}
                    posting={this.state.posting}
                    autoFocus={pinned}
                    sorceryCards={this.props.sorceryCards}
                    onOpenCard={this.props.onOpenCard}
                    onCardLinkHover={this.props.onCardHover}
                    onCardLinkHoverEnd={this.props.onCardHoverEnd}
                  />
                </div>
                )}
              </div>
            </>
          );
        })() : null}

        {/* ── Viewpoint swap: sit at the other seat's side of the table.
            Everything stays visible either way; this only flips the board
            orientation and which hand fans out at the bottom. ── */}
        <button
          type="button"
          title={t("View the board from {name}'s side", { name: this._name(otherSeat) })}
          className="absolute flex items-center gap-2 cursor-pointer pointer-events-auto transition-colors"
          style={{
            top: '0.875cqw', left: `calc(0.75cqw + ${SIDEBAR_WIDTH} + 0.625cqw)`,
            padding: '0.4375cqw 0.75cqw', borderRadius: 'var(--radius-lg)',
            background: 'rgba(14,11,7,0.85)', backdropFilter: 'blur(10px)',
            border: `1px solid ${GOLD} 0.22)`, color: TEXT_BODY, fontSize: '0.75cqw',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.45)`; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${GOLD} 0.22)`; }}
          disabled={!!this.state.veil}
          onClick={this.beginViewpointSwap}
        >
          {this.seatAvatars[viewpoint] ? (
            <AvatarImage src={this.seatAvatars[viewpoint]} className="rounded-full" style={{ width: '1.125cqw', height: '1.125cqw', border: `1px solid ${SEAT_COLORS[viewpoint]}` }} />
          ) : null}
          <span style={{ color: TEXT_PRIMARY, fontWeight: 500 }}>{t("{name}'s side", { name: this._name(viewpoint) })}</span>
          <svg style={{ width: '0.8125cqw', height: '0.8125cqw' }} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5.5h8.5M9 2.5l3 3-3 3M13 10.5H4.5M7 7.5l-3 3 3 3" /></svg>
        </button>

      </div>
    );
  }
}
